import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Plus, MessageSquare, Clock, CheckCircle, AlertTriangle, ChevronRight, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const PRIORITY_CONFIG = {
  low:    { label: 'Low',    color: 'text-gray-400',   bg: 'bg-gray-500/10 border-gray-500/20' },
  normal: { label: 'Normal', color: 'text-blue-400',   bg: 'bg-blue-500/10 border-blue-500/20' },
  high:   { label: 'High',   color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20' },
  urgent: { label: 'Urgent', color: 'text-red-400',    bg: 'bg-red-500/10 border-red-500/20' },
};

const CATEGORY_EMOJI = { general: '💬', billing: '💳', technical: '🔧', abuse: '🚨' };

const TicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [servers, setServers] = useState([]);
  const [form, setForm] = useState({ subject: '', message: '', category: 'general', priority: 'normal', server_id: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/tickets'),
      api.get('/servers'),
    ]).then(([t, s]) => {
      setTickets(t.data.tickets);
      setServers(s.data.servers);
    }).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/tickets', form);
      setTickets((prev) => [{ ...data.ticket, reply_count: 1 }, ...prev]);
      setShowNew(false);
      setForm({ subject: '', message: '', category: 'general', priority: 'normal', server_id: '' });
      toast.success('Ticket opened! We\'ll get back to you soon.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to open ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const openTickets = tickets.filter(t => t.status === 'open');
  const closedTickets = tickets.filter(t => t.status === 'closed');

  return (
    <div className="min-h-screen bg-gray-950 pt-24 pb-16 px-4">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-white">Support Tickets</h1>
            <p className="text-gray-400 mt-1">We typically reply within 2 hours</p>
          </div>
          <button
            onClick={() => setShowNew(!showNew)}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-4 py-2.5 rounded-xl text-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> New Ticket
          </button>
        </div>

        {/* New ticket form */}
        {showNew && (
          <div className="bg-gray-900 border border-green-500/30 rounded-2xl p-6 mb-8">
            <h2 className="font-bold text-white mb-5">Open a new ticket</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Subject</label>
                <input
                  type="text"
                  required
                  maxLength={255}
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500 transition-colors"
                  >
                    <option value="general">💬 General</option>
                    <option value="billing">💳 Billing</option>
                    <option value="technical">🔧 Technical</option>
                    <option value="abuse">🚨 Abuse Report</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500 transition-colors"
                  >
                    <option value="low">🟢 Low</option>
                    <option value="normal">🔵 Normal</option>
                    <option value="high">🟡 High</option>
                    <option value="urgent">🔴 Urgent</option>
                  </select>
                </div>
              </div>

              {servers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Related server <span className="text-gray-500">(optional)</span></label>
                  <select
                    value={form.server_id}
                    onChange={(e) => setForm({ ...form, server_id: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500 transition-colors"
                  >
                    <option value="">— Not server related —</option>
                    {servers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Message</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Describe your issue in detail..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-green-500 hover:bg-green-400 disabled:opacity-50 text-gray-950 font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
                >
                  {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Submit Ticket
                </button>
                <button type="button" onClick={() => setShowNew(false)} className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-sm transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader className="w-8 h-8 text-green-400 animate-spin" /></div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-20 bg-gray-900 border border-gray-800 border-dashed rounded-2xl">
            <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-white mb-1">No tickets yet</h2>
            <p className="text-gray-400 mb-6">Have an issue? Open a ticket and we'll help you out.</p>
            <button onClick={() => setShowNew(true)} className="bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-5 py-2.5 rounded-xl text-sm transition-colors">
              Open First Ticket
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Open tickets */}
            {openTickets.length > 0 && (
              <div>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Open ({openTickets.length})
                </h2>
                <div className="space-y-2">
                  {openTickets.map((ticket) => (
                    <TicketRow key={ticket.id} ticket={ticket} />
                  ))}
                </div>
              </div>
            )}

            {/* Closed tickets */}
            {closedTickets.length > 0 && (
              <div>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Closed ({closedTickets.length})
                </h2>
                <div className="space-y-2 opacity-60">
                  {closedTickets.map((ticket) => (
                    <TicketRow key={ticket.id} ticket={ticket} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TicketRow = ({ ticket }) => {
  const priority = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.normal;
  return (
    <Link
      to={`/tickets/${ticket.id}`}
      className="flex items-center justify-between bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-2xl p-5 transition-colors group"
    >
      <div className="flex items-center gap-4 min-w-0">
        <div className="text-xl flex-shrink-0">{CATEGORY_EMOJI[ticket.category] || '💬'}</div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-white truncate">{ticket.subject}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${priority.bg} ${priority.color}`}>
              {priority.label}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>#{ticket.id.slice(0, 8).toUpperCase()}</span>
            <span>·</span>
            <span>{ticket.reply_count || 0} {ticket.reply_count === 1 ? 'message' : 'messages'}</span>
            <span>·</span>
            <span>{new Date(ticket.updated_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
        {ticket.status === 'open'
          ? <div className="flex items-center gap-1.5 text-green-400 text-xs"><span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />Open</div>
          : <div className="flex items-center gap-1.5 text-gray-500 text-xs"><CheckCircle className="w-3.5 h-3.5" />Closed</div>
        }
        <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-400 transition-colors" />
      </div>
    </Link>
  );
};

export default TicketsPage;
